import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type {
  ShortcutLink,
  DashboardConfig,
  Email,
  Note,
  Task,
  Schedule,
} from "@/types/models";
import {
  loadFromStorage,
  saveToStorage,
  STORAGE_KEYS,
  loadNotes,
  saveNotes,
  loadTasks,
  saveTasks,
  loadSchedules,
  saveSchedules,
} from "@/utils/storage";

const DEFAULT_LINKS: ShortcutLink[] = [
  { name: "GitHub", url: "https://github.com", icon: "fab fa-github" },
  { name: "Notion", url: "https://www.notion.so", icon: "fas fa-note-sticky" },
  {
    name: "ChatGPT",
    url: "https://chat.openai.com",
    icon: "fas fa-comment-dots",
  },
];

export const useDashboardStore = defineStore("dashboard", () => {
  // State
  const links = ref<ShortcutLink[]>(
    loadFromStorage(STORAGE_KEYS.SHORTCUT_LINKS, DEFAULT_LINKS),
  );
  const backgroundImage = ref<string | null>(
    loadFromStorage(STORAGE_KEYS.BG_IMAGE, null),
  );
  const isEditMode = ref(false);
  const isGmailSidebarOpen = ref(false);
  const emails = ref<Email[]>([]);
  const isLoadingEmails = ref(false);
  const notes = ref<Note[]>(loadNotes<Note[]>([]));
  const tasks = ref<Task[]>(loadTasks<Task[]>([]));
  const schedules = ref<Schedule[]>(loadSchedules<Schedule[]>([]));
  const isLoadingNotes = ref(false);
  const isLoadingTasks = ref(false);
  const isLoadingSchedules = ref(false);
  const isNotesVisible = ref<boolean>(
    loadFromStorage(STORAGE_KEYS.NOTES_VISIBLE, true),
  );
  const isTasksVisible = ref<boolean>(
    loadFromStorage(STORAGE_KEYS.TASKS_VISIBLE, true),
  );
  const isSchedulesVisible = ref<boolean>(
    loadFromStorage(STORAGE_KEYS.SCHEDULES_VISIBLE, true),
  );
  const isCalendarVisible = ref<boolean>(
    loadFromStorage(STORAGE_KEYS.CALENDAR_VISIBLE, false),
  );

  // Computed
  const hasCustomLinks = computed(() => {
    const defaultNames = new Set(DEFAULT_LINKS.map((l) => l.name));
    return links.value.some((l) => !defaultNames.has(l.name));
  });

  // Methods
  const getLinks = (): ShortcutLink[] => {
    return links.value;
  };

  const addLink = (link: ShortcutLink): void => {
    // optimistic update
    links.value.push(link);
    saveToStorage(STORAGE_KEYS.SHORTCUT_LINKS, links.value);
    // persist to server, fallback to localStorage on failure
    fetch("/api/shortcuts", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(link),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.data) {
          // replace optimistic entry with server persisted one (id present)
          const idx = links.value.findIndex(
            (l) => l.url === link.url && l.name === link.name,
          );
          if (idx >= 0) links.value[idx] = data.data;
        }
      })
      .catch(() => {
        saveToStorage(STORAGE_KEYS.SHORTCUT_LINKS, links.value);
      });
  };

  const updateLink = (index: number, link: ShortcutLink): void => {
    if (index >= 0 && index < links.value.length) {
      const existing = links.value[index];
      links.value[index] = { ...existing, ...link };
      saveToStorage(STORAGE_KEYS.SHORTCUT_LINKS, links.value);
      // send update to server if id exists
      const id = (existing as any).id;
      if (id) {
        fetch(`/api/shortcuts/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(link),
        }).catch(() => {
          saveToStorage(STORAGE_KEYS.SHORTCUT_LINKS, links.value);
        });
      } else {
        saveToStorage(STORAGE_KEYS.SHORTCUT_LINKS, links.value);
      }
    }
  };

  const removeLink = (index: number): void => {
    if (index >= 0 && index < links.value.length) {
      const existing = links.value[index];
      links.value.splice(index, 1);
      saveToStorage(STORAGE_KEYS.SHORTCUT_LINKS, links.value);
      const id = (existing as any).id;
      if (id) {
        fetch(`/api/shortcuts/${id}`, { method: "DELETE" }).catch(() => {
          saveToStorage(STORAGE_KEYS.SHORTCUT_LINKS, links.value);
        });
      } else {
        saveToStorage(STORAGE_KEYS.SHORTCUT_LINKS, links.value);
      }
    }
  };

  const fetchNotes = async (): Promise<void> => {
    isLoadingNotes.value = true;
    try {
      const res = await fetch("/api/notes");
      if (res.ok) {
        const data = await res.json();
        if (data && data.data) {
          notes.value = data.data;
          saveNotes(notes.value);
          return;
        }
      }
    } catch (e) {
      // ignore and fallback
    } finally {
      isLoadingNotes.value = false;
    }

    notes.value = loadNotes<Note[]>([]);
  };

  const fetchTasks = async (): Promise<void> => {
    isLoadingTasks.value = true;
    try {
      const res = await fetch("/api/tasks");
      if (res.ok) {
        const data = await res.json();
        if (data && data.data) {
          tasks.value = data.data;
          saveTasks(tasks.value);
          return;
        }
      }
    } catch (e) {
      // ignore and fallback
    } finally {
      isLoadingTasks.value = false;
    }

    tasks.value = loadTasks<Task[]>([]);
  };

  const fetchSchedules = async (): Promise<void> => {
    isLoadingSchedules.value = true;
    try {
      const res = await fetch("/api/schedules");
      if (res.ok) {
        const data = await res.json();
        if (data && data.data) {
          schedules.value = data.data;
          saveSchedules(schedules.value);
          return;
        }
      }
    } catch (e) {
      // ignore and fallback
    } finally {
      isLoadingSchedules.value = false;
    }

    schedules.value = loadSchedules<Schedule[]>([]);
  };

  const addNote = (note: Note): void => {
    notes.value.push(note);
    saveNotes(notes.value);
    fetch("/api/notes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(note),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.data) {
          const idx = notes.value.findIndex(
            (n) => n.title === note.title && n.content === note.content,
          );
          if (idx >= 0) notes.value[idx] = data.data;
          saveNotes(notes.value);
        }
      })
      .catch(() => {
        saveNotes(notes.value);
      });
  };

  const updateNote = (index: number, note: Note): void => {
    if (index < 0 || index >= notes.value.length) return;
    const existing = notes.value[index];
    notes.value[index] = { ...existing, ...note };
    saveNotes(notes.value);
    const id = (existing as Note).id;
    if (id) {
      fetch(`/api/notes/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(note),
      }).catch(() => {
        saveNotes(notes.value);
      });
    }
  };

  const removeNote = (index: number): void => {
    if (index < 0 || index >= notes.value.length) return;
    const existing = notes.value[index];
    notes.value.splice(index, 1);
    saveNotes(notes.value);
    const id = (existing as Note).id;
    if (id) {
      fetch(`/api/notes/${id}`, { method: "DELETE" }).catch(() => {
        saveNotes(notes.value);
      });
    }
  };

  const addTask = (task: Task): void => {
    tasks.value.push(task);
    saveTasks(tasks.value);
    fetch("/api/tasks", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(task),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.data) {
          const idx = tasks.value.findIndex(
            (t) => t.title === task.title && t.completed === task.completed,
          );
          if (idx >= 0) tasks.value[idx] = data.data;
          saveTasks(tasks.value);
        }
      })
      .catch(() => {
        saveTasks(tasks.value);
      });
  };

  const updateTask = (index: number, task: Task): void => {
    if (index < 0 || index >= tasks.value.length) return;
    const existing = tasks.value[index];
    tasks.value[index] = { ...existing, ...task };
    saveTasks(tasks.value);
    const id = (existing as Task).id;
    if (id) {
      fetch(`/api/tasks/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(task),
      }).catch(() => {
        saveTasks(tasks.value);
      });
    }
  };

  const updateSchedule = (
    index: number,
    payload: { title: string; datetime: string | null },
  ): void => {
    if (index < 0 || index >= schedules.value.length) return;
    const existing = schedules.value[index];
    const updated = {
      ...existing,
      title: payload.title,
    } as Schedule;
    schedules.value[index] = updated;
    saveSchedules(schedules.value);
    const id = (existing as Schedule).id;
    if (id) {
      fetch(`/api/schedules/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).catch(() => {
        saveSchedules(schedules.value);
      });
    }
  };

  const removeSchedule = (index: number): void => {
    if (index < 0 || index >= schedules.value.length) return;
    const existing = schedules.value[index];
    schedules.value.splice(index, 1);
    saveSchedules(schedules.value);
    const id = (existing as Schedule).id;
    if (id) {
      fetch(`/api/schedules/${id}`, { method: "DELETE" }).catch(() => {
        saveSchedules(schedules.value);
      });
    }
  };

  const toggleTaskCompleted = (index: number): void => {
    if (index < 0 || index >= tasks.value.length) return;
    const existing = tasks.value[index];
    updateTask(index, { ...existing, completed: !existing.completed });
  };

  const removeTask = (index: number): void => {
    if (index < 0 || index >= tasks.value.length) return;
    const existing = tasks.value[index];
    tasks.value.splice(index, 1);
    saveTasks(tasks.value);
    const id = (existing as Task).id;
    if (id) {
      fetch(`/api/tasks/${id}`, { method: "DELETE" }).catch(() => {
        saveTasks(tasks.value);
      });
    }
  };

  // initialize store from server; fallback to localStorage
  const init = async (): Promise<void> => {
    await Promise.all([fetchNotes(), fetchTasks(), fetchSchedules()]);
    try {
      const res = await fetch("/api/shortcuts");
      if (res.ok) {
        const data = await res.json();
        if (data && data.data) {
          links.value = data.data;
          return;
        }
      }
    } catch (e) {
      // ignore and fallback
    }

    // fallback to localStorage persisted links
    links.value = loadFromStorage(STORAGE_KEYS.SHORTCUT_LINKS, DEFAULT_LINKS);
  };

  const setBackgroundImage = (image: string | null): void => {
    backgroundImage.value = image;
    if (image) {
      saveToStorage(STORAGE_KEYS.BG_IMAGE, image);
    } else {
      localStorage.removeItem(STORAGE_KEYS.BG_IMAGE);
    }
  };

  const toggleEditMode = (): void => {
    isEditMode.value = !isEditMode.value;
  };

  const toggleGmailSidebar = (): void => {
    isGmailSidebarOpen.value = !isGmailSidebarOpen.value;
  };

  const closeGmailSidebar = (): void => {
    isGmailSidebarOpen.value = false;
  };

  const setEmails = (newEmails: Email[]): void => {
    emails.value = newEmails;
  };

  const setLoadingEmails = (loading: boolean): void => {
    isLoadingEmails.value = loading;
  };

  const toggleNotesVisible = (): void => {
    isNotesVisible.value = !isNotesVisible.value;
    saveToStorage(STORAGE_KEYS.NOTES_VISIBLE, isNotesVisible.value);
  };

  const toggleTasksVisible = (): void => {
    isTasksVisible.value = !isTasksVisible.value;
    saveToStorage(STORAGE_KEYS.TASKS_VISIBLE, isTasksVisible.value);
  };

  const toggleSchedulesVisible = (): void => {
    isSchedulesVisible.value = !isSchedulesVisible.value;
    saveToStorage(STORAGE_KEYS.SCHEDULES_VISIBLE, isSchedulesVisible.value);
  };

  const toggleCalendarVisible = (): void => {
    isCalendarVisible.value = !isCalendarVisible.value;
    saveToStorage(STORAGE_KEYS.CALENDAR_VISIBLE, isCalendarVisible.value);
  };

  return {
    // State
    links,
    backgroundImage,
    isEditMode,
    isGmailSidebarOpen,
    emails,
    isLoadingEmails,
    notes,
    tasks,
    schedules,
    isLoadingNotes,
    isLoadingTasks,
    isLoadingSchedules,
    isNotesVisible,
    isTasksVisible,
    isSchedulesVisible,
    isCalendarVisible,
    // Computed
    hasCustomLinks,
    // Methods
    getLinks,
    addLink,
    updateLink,
    removeLink,
    setBackgroundImage,
    toggleEditMode,
    toggleGmailSidebar,
    closeGmailSidebar,
    setEmails,
    setLoadingEmails,
    toggleNotesVisible,
    toggleTasksVisible,
    toggleSchedulesVisible,
    toggleCalendarVisible,
    fetchNotes,
    fetchTasks,
    fetchSchedules,
    addNote,
    updateNote,
    removeNote,
    addTask,
    updateTask,
    updateSchedule,
    toggleTaskCompleted,
    removeTask,
    removeSchedule,
    init,
  };
});
